import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { comics, categories } from '@/mocks/comics';

export default function Home() {
  const [activeCategory, setActiveCategory] = useState('all');

  const featuredComics = comics.slice(0, 4);
  const latestComics = [...comics].sort((a, b) =>
    new Date(b.chapters[b.chapters.length - 1].updateDate).getTime() -
    new Date(a.chapters[a.chapters.length - 1].updateDate).getTime()
  );
  const filteredComics = activeCategory === 'all'
    ? comics
    : comics.filter(c => c.category === activeCategory);

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '韩漫屋',
    url: window.location.origin + (__BASE_PATH__ || ''),
    description: '韩漫屋提供无遮韩国免费漫画大全，支持完整的免费在线阅读服务，资源齐全无删减，满足您的所有阅读需求。',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${window.location.origin}${__BASE_PATH__ || ''}/category/{category_name}`,
      },
      'query-input': 'required name=category_name',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫屋',
    url: window.location.origin + (__BASE_PATH__ || ''),
    description: '韩漫屋提供无遮韩国免费漫画大全，支持完整的免费在线阅读服务。',
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: window.location.origin + (__BASE_PATH__ || ''),
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20beautiful%20artistic%20abstract%20illustration%20with%20soft%20warm%20gradients%20of%20peach%20and%20cream%20colors%2C%20delicate%20flowing%20shapes%20resembling%20comic%20panels%20and%20speech%20bubbles%2C%20clean%20minimalist%20style%2C%20elegant%20composition%2C%20no%20text%2C%20light%20airy%20atmosphere%2C%20artistic%20background%20for%20webtoon%20website&width=1600&height=900&seq=hero-bg-01&orientation=landscape"
            alt="韩漫屋 - 免费在线阅读韩国漫画"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-3 md:mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              韩漫屋 - 无遮韩国免费漫画大全
            </h1>
            <p className="text-white/80 text-sm md:text-base max-w-xl mb-6 md:mb-8 leading-relaxed">
              提供完整的无遮韩国漫画免费在线阅读服务，资源齐全无删减，随时随地畅享韩漫阅读体验
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/category/all"
                className="px-6 py-2.5 bg-accent-500 text-background-50 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap hover:opacity-90 transition-opacity"
              >
                开始阅读
              </Link>
              <Link
                to="/about"
                className="px-6 py-2.5 border border-white/40 text-white rounded-full text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-white/10 transition-colors"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Featured Section */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg md:text-xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                热门推荐
              </h2>
              <Link to="/category/all" className="text-accent-500 text-sm cursor-pointer whitespace-nowrap hover:underline">
                查看全部 <i className="ri-arrow-right-line ml-1 text-xs"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {featuredComics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-100 mb-3">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      title={`${comic.title} - 韩漫屋免费在线阅读`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-2 left-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                        comic.status === '连载中'
                          ? 'bg-accent-500 text-background-50'
                          : 'bg-foreground-600 text-background-50'
                      }`}>
                        {comic.status}
                      </span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                      <div className="flex items-center gap-2 text-white text-xs">
                        <span><i className="ri-star-fill text-accent-500 mr-0.5"></i>{comic.rating}</span>
                        <span><i className="ri-eye-line mr-0.5"></i>{(comic.views / 10000).toFixed(0)}万</span>
                      </div>
                    </div>
                  </div>
                  <h3 className="text-sm md:text-base font-medium text-foreground-950 group-hover:text-accent-500 transition-colors line-clamp-1">
                    {comic.title}
                  </h3>
                  <p className="text-xs text-foreground-600 mt-0.5">{comic.author}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Category Filter Section */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100/70">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              漫画分类
            </h2>
            <div className="flex flex-wrap gap-2 mb-8">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`whitespace-nowrap px-4 py-2 rounded-full text-sm cursor-pointer transition-colors flex items-center gap-1.5 ${
                    activeCategory === cat.slug
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-background-50 text-foreground-700 hover:bg-accent-100 hover:text-accent-700'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {filteredComics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200/70 mb-3">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      title={`${comic.title} - 韩漫屋${categories.find(c => c.slug === comic.category)?.name || ''}漫画`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-2 left-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                        comic.status === '连载中'
                          ? 'bg-accent-500 text-background-50'
                          : 'bg-foreground-600 text-background-50'
                      }`}>
                        {comic.status}
                      </span>
                    </div>
                  </div>
                  <h3 className="text-sm md:text-base font-medium text-foreground-950 group-hover:text-accent-500 transition-colors line-clamp-1">
                    {comic.title}
                  </h3>
                  <div className="flex items-center gap-2 mt-0.5 text-xs text-foreground-600">
                    <span>{comic.author}</span>
                    <span>·</span>
                    <span>{categories.find(c => c.slug === comic.category)?.name}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates Section */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg md:text-xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                最近更新
              </h2>
            </div>
            <div className="space-y-3">
              {latestComics.map((comic) => {
                const latestChapter = comic.chapters[comic.chapters.length - 1];
                return (
                  <Link
                    key={comic.id}
                    to={`/comic/${comic.id}`}
                    className="flex items-center gap-3 md:gap-4 p-3 rounded-lg bg-background-100/70 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <div className="w-12 h-16 md:w-14 md:h-20 rounded overflow-hidden bg-background-200/70 flex-shrink-0">
                      <img
                        src={comic.cover}
                        alt={comic.title}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-foreground-950 line-clamp-1">{comic.title}</h3>
                      <p className="text-xs text-foreground-600 mt-0.5">{comic.author}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs text-accent-500 font-medium">{latestChapter.title}</p>
                      <p className="text-xs text-foreground-500 mt-0.5">{latestChapter.updateDate}</p>
                    </div>
                    <div className="hidden sm:flex items-center justify-center w-8 h-8 rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-arrow-right-s-line"></i>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100/70">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 text-center">
              <div className="p-4 md:p-6">
                <div className="text-2xl md:text-3xl font-bold text-accent-500" style={{ fontFamily: 'var(--font-heading)' }}>5000+</div>
                <p className="text-xs md:text-sm text-foreground-600 mt-1">漫画资源</p>
              </div>
              <div className="p-4 md:p-6">
                <div className="text-2xl md:text-3xl font-bold text-accent-500" style={{ fontFamily: 'var(--font-heading)' }}>100万+</div>
                <p className="text-xs md:text-sm text-foreground-600 mt-1">月活跃读者</p>
              </div>
              <div className="p-4 md:p-6">
                <div className="text-2xl md:text-3xl font-bold text-accent-500" style={{ fontFamily: 'var(--font-heading)' }}>每日更新</div>
                <p className="text-xs md:text-sm text-foreground-600 mt-1">实时同步</p>
              </div>
              <div className="p-4 md:p-6">
                <div className="text-2xl md:text-3xl font-bold text-accent-500" style={{ fontFamily: 'var(--font-heading)' }}>完全免费</div>
                <p className="text-xs md:text-sm text-foreground-600 mt-1">无需注册</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto text-center">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
              准备好开始阅读了吗？
            </h2>
            <p className="text-foreground-600 text-sm max-w-md mx-auto mb-6">
              数千部韩国漫画等你来发现，全部免费完整阅读，现在就开启你的韩漫之旅吧！
            </p>
            <Link
              to="/category/all"
              className="inline-block px-6 py-2.5 bg-accent-500 text-background-50 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap hover:opacity-90 transition-opacity"
            >
              浏览全部漫画
            </Link>
          </div>
        </section>
      </main>

      <Footer />

      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
    </>
  );
}